import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  ArrowLeft,
  Calendar,
  User,
  ArrowRight,
  Phone,
  Rss
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// AutoSEO RSS Feed for subscription
const AUTOSEO_RSS_FEED = "https://getautoseo.com/feeds/16337/fDEPBi_dSKoXzlNQpx75y7uktyi5rtcfzL_gCKCuu_I.rss";

export default function BlogPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      // Fetch single post
      axios.get(`${API}/blog/${slug}`)
        .then(res => {
          setPost(res.data);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      // Fetch all posts from local database
      axios.get(`${API}/blog`)
        .then(res => {
          setPosts(res.data || []);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  // Single post view
  if (slug) {
    if (loading) {
      return (
        <div className="pt-20 min-h-screen bg-white flex items-center justify-center">
          <p className="text-slate-500">Loading...</p>
        </div>
      );
    }

    if (!post) {
      return (
        <div className="pt-20 min-h-screen bg-white">
          <div className="container-main py-20 text-center">
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">Post Not Found</h1>
            <Link to="/blog">
              <Button className="btn-gold">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="pt-20 min-h-screen bg-white" data-testid="blog-post-page">
        {/* Hero */}
        <section className="bg-slate-900 py-16">
          <div className="container-main">
            <Link to="/blog" className="inline-flex items-center text-amber-500 hover:text-amber-400 mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-slate-400">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="section-padding">
          <div className="container-main">
            <div className="max-w-3xl mx-auto">
              <div 
                className="prose prose-lg prose-slate max-w-none font-body"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
              
              {/* CTA */}
              <div className="mt-12 p-8 bg-slate-900 text-center">
                <h3 className="font-heading text-2xl font-bold text-white mb-4">
                  Ready to Get Funded?
                </h3>
                <p className="text-slate-300 mb-6">
                  Contact us today to discuss your hard money loan needs.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/apply">
                    <Button className="btn-gold">
                      Apply Now
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <a href="tel:424-303-2130">
                    <Button variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                      <Phone className="w-4 h-4 mr-2" />
                      424-303-2130
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Blog listing view
  return (
    <div className="pt-20 min-h-screen bg-slate-50" data-testid="blog-page">
      {/* Hero */}
      <section className="bg-slate-900 py-20">
        <div className="container-main">
          <div className="max-w-3xl">
            <p className="font-body text-amber-500 uppercase tracking-widest text-sm font-semibold mb-4">
              INSIGHTS & RESOURCES
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              {language === 'en' ? 'Blog' : '博客'}
            </h1>
            <p className="font-body text-slate-300 text-lg">
              Expert insights on hard money lending, real estate investing, and private financing.
            </p>
            <div className="mt-6 flex items-center gap-6">
              <a 
                href={AUTOSEO_RSS_FEED}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-body text-sm"
              >
                <Rss className="w-4 h-4" />
                Subscribe to RSS Feed
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="section-padding">
        <div className="container-main">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500 mb-4">No blog posts yet.</p>
              <p className="text-slate-400 text-sm">Check back soon for expert insights on hard money lending.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <article 
                  key={post.id || index} 
                  className="bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                    <h2 className="font-heading text-xl font-bold text-slate-900 mb-3 line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="font-body text-slate-600 mb-4 line-clamp-3">
                      {post.excerpt || post.content?.replace(/<[^>]*>/g, '').slice(0, 150)}...
                    </p>
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-amber-600 hover:text-amber-700 font-semibold"
                    >
                      Read More
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-slate-900">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            Need Funding for Your Next Project?
          </h2>
          <p className="font-body text-slate-300 mb-8 max-w-2xl mx-auto">
            Get funded in as little as 5-7 days. No appraisal required.
          </p>
          <Link to="/apply">
            <Button size="lg" className="btn-gold">
              Apply Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
